import React, { useEffect, useState } from "react";
import { Button, Divider, Form, FormGroup, Grid, Header, Icon, Modal, Segment, Statistic, Table, Message, Label } from "semantic-ui-react";
import { format } from "date-fns";
import { toast } from "react-toastify";
import GaugeChart from "react-gauge-chart";

import ModalApp from "../../components/ModalApp";
import { eStatusConsulta, eTipoOcorrencia, IConsulta } from "../../interfaces";
import NumberFormat from "../../Utils/NumberFormat";
import errors from "../../Utils/ExtractErrors";
import ConsultaRepository from "../../Repositories/ConsultaRepository";

interface IProps {
    idConsulta: string;
    onSuccess?: VoidFunction;
}

interface IState {
    open: boolean;
    loading: boolean;
    showAnalise: boolean;
    consulta: IConsulta;
    approving: boolean;
    disapproving: boolean;
    trustApproving: boolean;
}

const initialState: IState = {
    open: false,
    loading: false,
    showAnalise: false,
    consulta: {
        id: "",
        dataConsulta: "",
        cpf: "",
        nome: "",
        dataNascimento: "",
        idConsulta: "",
        score: {
            numeroScore: 0,
            nomeScore: "",
            texto: ""
        },
        debitos: {
            resumo: {
                totalDevedor: "",
                totalAvalista: "",
                valorAcumulado: 0,
                dataPrimeiroDebito: "",
                valorPrimeiroDebito: 0,
                dataMaiorDebito: "",
                valorMaiorDebito: 0
            },
            titulos: []
        },
        chequeTalaoSustados: [],
        chequeSustadosMotivo21: [],
        consultasAnteriores: [],
        representanteId: "",
        status: {
            id: 0,
            descricao: ""
        }
    },
    approving: false,
    disapproving: false,
    trustApproving: false
}

export default function ConsultDetails(props: IProps) {
    const [state, setState] = useState<IState>(initialState);
    const [openConfirm, setOpenConfirm] = useState<boolean>(false);
    const [disableButton, setDisabled] = useState<boolean>(false);
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const totalDebitos = state.consulta.debitos.titulos.reduce((a, b) => a + b.valor, 0);

    useEffect(() => {
        if (!state.open) {
            setState(initialState);
        }
    }, [state.open]);

    function handleOpen() {
        setState(state => ({ ...state, open: true }));
        load();
    }

    function handleClose() {
        setState(initialState);
    }

    const handleOnChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(e.target.value);
    }

    const handleConfirmPassword = async () => {
        if (confirmPassword === "secretpass") {
            await handleTrustApprove();
            setOpenConfirm(false);
        } else {
            toast("🤷‍♂️ Senha inválida.", { position: "top-center", type: "error" });
            setConfirmPassword("");
        }

    }

    async function handleApprove() {
        setState(state => ({ ...state, approving: true }));
        try {
            const response = await ConsultaRepository.Approve(state.consulta.cpf);
            setState(initialState);
            if (props.onSuccess) {
                props.onSuccess();
            }
            toast(response.message, { position: "top-center", type: "success" });
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });

            setState(state => ({ ...state, approving: false }));
        }
    }

    async function handleDisapprove() {
        setState(state => ({ ...state, disapproving: true }));
        try {
            const response = await ConsultaRepository.Disapprove(state.consulta.cpf);
            setState(initialState);
            if (props.onSuccess) {
                props.onSuccess();
            }
            toast(response.message, { position: "top-center", type: "success" });
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });

            setState(state => ({ ...state, disapproving: false }));
        }
    }

    async function handleTrustApprove() {
        setState(state => ({ ...state, trustApproving: true }));
        try {
            const response = await ConsultaRepository.TrustApprove(state.consulta.cpf);
            setState(initialState);
            if (props.onSuccess) {
                props.onSuccess();
            }
            toast(response.message, { position: "top-center", type: "success" });
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });

            setState(state => ({ ...state, trustApproving: false }));
        }
    }

    async function load() {
        setState(state => ({ ...state, loading: true }));
        try {
            const response = await ConsultaRepository.GetById(props.idConsulta);

            setState(state => ({
                ...state,
                consulta: response,
                showAnalise: response.status?.id === eStatusConsulta.Analise,
                loading: false
            }));
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });

            setState(state => ({ ...state, loading: false }));
        }
    }

    const setColor = () => {
        const { numeroScore } = state.consulta.score;

        if (numeroScore <= 200) {
            return "red";
        }
        if (numeroScore <= 400) {
            return "orange";
        }
        if (numeroScore <= 600) {
            return "yellow";
        }
        if (numeroScore <= 800) {
            return "olive";
        }
        if (numeroScore < 1000) {
            return "green";
        }
        return "pink";
    };

    const CalcAge = () => {
        const nascimento = new Date(state.consulta.dataNascimento);
        const diferenca = Date.now() - nascimento.getTime();
        const idade = new Date(diferenca);
        return Math.abs(idade.getUTCFullYear() - 1970)
    }

    const regionAnalise = (
        <>
            {state.showAnalise &&
                <>
                    <Button.Group fluid>
                        <Button
                            negative
                            onClick={handleDisapprove}
                            loading={state.disapproving}
                        >
                            REPROVAR
                        </Button>
                        <Button.Or text="?" />
                        <Button
                            positive
                            onClick={handleApprove}
                            loading={state.approving}
                        >
                            APROVAR
                        </Button>
                    </Button.Group>
                    <Divider />
                </>
            }
            {state.consulta.status.id === eStatusConsulta.Reprovado &&
                <>
                    <ModalApp
                        content={
                            <Modal.Content>
                                <Message
                                    color="red"
                                    content="Para continuar com a liberação em confiança, digite a senha de ADM"
                                    icon="question"
                                />
                                <Form>
                                    <Form.Input
                                        label="Senha"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={handleOnChangePassword}
                                    />
                                </Form>
                            </Modal.Content>
                        }
                        title="Atenção"
                        open={openConfirm}
                        handleClose={() => setOpenConfirm(false)}
                        onSuccess={{
                            disabled: disableButton,
                            content: "Continuar",
                            func: handleConfirmPassword,
                        }}
                    />
                    <Button.Group fluid>
                        <Button
                            negative
                            onClick={() => setOpenConfirm(true)}
                            loading={state.trustApproving}
                        ><Icon name="warning sign" size="large" />
                            LIBERAR EM CONFIANÇA
                        </Button>
                    </Button.Group>
                    <Divider />
                </>
            }
        </>
    );

    const regionConsumerData = (
        <Segment color="pink">
            <Form>
                <FormGroup widths="equal">
                    <Form.Input
                        readOnly
                        fluid
                        label="NOME"
                        icon="user"
                        iconPosition="left"
                        value={state.consulta.nome}
                    />
                    <Form.Input
                        readOnly
                        fluid
                        label="CPF"
                        icon="id card"
                        iconPosition="left"
                        value={state.consulta.cpf}
                    />
                </FormGroup>
                <FormGroup widths="equal">
                    <Form.Input
                        readOnly
                        fluid
                        icon="birthday"
                        iconPosition="left"
                        label="DATA NASC"
                        value={state.consulta.dataNascimento.length > 0 ? format(new Date(state.consulta.dataNascimento), "dd/MM/yyyy") : ""}
                    />
                    <Form.Input
                        readOnly
                        fluid
                        label="IDADE"
                        icon="slack"
                        iconPosition="left"
                        value={CalcAge()}
                    />
                </FormGroup>
            </Form>
        </Segment>
    );

    const regionResume = (
        <Segment color="pink" textAlign="center">
            <Grid columns={3}>
                <Grid.Column>
                    <Statistic size="mini">
                        <Statistic.Label content="QTD. CONSULTAS"></Statistic.Label>
                        <Statistic.Value>
                            {state.consulta.consultasAnteriores.length}
                        </Statistic.Value>
                    </Statistic>
                </Grid.Column>
                <Grid.Column>
                    <Statistic size="mini">
                        <Statistic.Label content="QTD. DÉBITOS"></Statistic.Label>
                        <Statistic.Value>
                            {state.consulta.debitos.titulos.length}
                        </Statistic.Value>
                    </Statistic>
                </Grid.Column>
                <Grid.Column>
                    <Statistic color="red" size="mini">
                        <Statistic.Label content="TOTAL DÉBITOS"></Statistic.Label>
                        <Statistic.Value>
                            {NumberFormat.Currency(totalDebitos)}
                        </Statistic.Value>
                    </Statistic>
                </Grid.Column>
            </Grid>
        </Segment>
    )

    const regionScore = (
        <Segment color="pink" placeholder>
            <Grid columns={2} textAlign="center" stackable>
                <Grid.Column>
                    <Statistic color={setColor()}>
                        <Statistic.Value content={state.consulta.score.numeroScore}></Statistic.Value>
                    </Statistic>
                    <GaugeChart
                        id="gauge-chart1"
                        textColor="black"
                        nrOfLevels={5}
                        colors={['#EA4228', '#F5CD19', '#5BE12C']}
                        percent={(state.consulta.score.numeroScore) / 1000}
                        hideText={true}
                        formatTextValue={value => value}
                        animate={true}
                        animDelay={0}
                    />
                </Grid.Column>
                <Grid.Column verticalAlign="middle">
                    <Header textAlign="justified">
                        {state.consulta.score.texto}
                    </Header>
                </Grid.Column>
            </Grid>
        </Segment>
    );

    const regionDebits = (
        <>
            {state.consulta.debitos?.titulos.filter(titulo => titulo.ocorrencia.id !== eTipoOcorrencia.TP).length > 0 &&
                <Table selectable striped color="pink">
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell colSpan="3">
                                <Icon name="dollar sign" size="large" />
                                REGISTRO DE DÉBITOS:
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {state.consulta.debitos.titulos.filter(titulo => titulo.ocorrencia.id !== eTipoOcorrencia.TP).map(titulo =>
                            <Table.Row>
                                <Table.Cell textAlign="left">{format(new Date(titulo.dataOcorrencia), "dd/MM/yyyy")}</Table.Cell>
                                <Table.Cell textAlign="left">{titulo.informante}</Table.Cell>
                                <Table.Cell textAlign="right">{NumberFormat.Currency(titulo.valor)}</Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            }
        </>
    );

    const regionPreviousConsults = (
        <>
            {state.consulta.consultasAnteriores?.length > 0 &&
                <Table selectable striped color="pink">
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell colSpan="2">
                                <Icon name="share" flipped="horizontally" size="large" />
                                CONSULTAS ANTERIORES:
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {state.consulta.consultasAnteriores.map(consulta =>
                            <Table.Row>
                                <Table.Cell textAlign="left">{format(new Date(consulta.data), "dd/MM/yyyy")}</Table.Cell>
                                <Table.Cell textAlign="left">{consulta.informante}</Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            }
        </>
    );

    const regionProtests = (
        <>
            {state.consulta.debitos?.titulos.filter(titulo => titulo.ocorrencia.id === eTipoOcorrencia.TP).length > 0 &&
                <Table selectable striped color="pink">
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell colSpan="4">
                                <Icon name="industry" size="large" />
                                PROTESTOS:
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {state.consulta.debitos.titulos.filter(titulo => titulo.ocorrencia.id === eTipoOcorrencia.TP).map(titulo =>
                            <Table.Row>
                                <Table.Cell textAlign="left">{format(new Date(titulo.dataOcorrencia), "dd/MM/yyyy")}</Table.Cell>
                                <Table.Cell>{titulo.cidade}</Table.Cell>
                                <Table.Cell>{titulo.uf}</Table.Cell>
                                <Table.Cell textAlign="right">{NumberFormat.Currency(titulo.valor)}</Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            }
        </>
    );

    const regionCheck = (
        <>
            {state.consulta.chequeTalaoSustados?.length > 0 &&
                <Table selectable striped color="pink">
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell colSpan="3">
                                <Icon name="industry" size="large" />
                                CHEQUES TALÃO SUSTADO:
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {state.consulta.chequeTalaoSustados.map(cheque =>
                            <Table.Row>
                                <Table.Cell textAlign="left">{format(new Date(cheque.dataOcorrencia), "dd/MM/yyyy")}</Table.Cell>
                                <Table.Cell textAlign="left">{cheque.informante}</Table.Cell>
                                <Table.Cell textAlign="left">{cheque.banco}</Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            }
        </>
    );

    const regionCheck21 = (
        <>
            {state.consulta.chequeSustadosMotivo21?.length > 0 &&
                <Table selectable striped color="pink">
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell colSpan="4">
                                <Icon name="industry" size="large" />
                                CHEQUES TALÃO SUSTADO:
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {state.consulta.chequeSustadosMotivo21.map(cheque =>
                            <Table.Row>
                                <Table.Cell textAlign="left">{format(new Date(cheque.dataOcorrencia), "dd/MM/yyyy")}</Table.Cell>
                                <Table.Cell textAlign="left">{cheque.informante}</Table.Cell>
                                <Table.Cell textAlign="left">{cheque.banco}</Table.Cell>
                                <Table.Cell textAlign="right">{NumberFormat.Currency(cheque.valor)}</Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            }
        </>
    );

    return (
        <ModalApp
            open={state.open}
            loading={state.loading}
            handleOpen={handleOpen}
            handleClose={handleClose}
            title={"Dados do consumidor"}
            content={
                <Modal.Content>
                    {regionAnalise}
                    {regionConsumerData}
                    {regionResume}
                    {regionScore}
                    {regionDebits}
                    {regionPreviousConsults}
                    {regionProtests}
                    {regionCheck}
                    {regionCheck21}
                </Modal.Content>}
            trigger={{
                icon: "search",
                color: "blue"
            }}
            cancelText="Fechar"
        />
    );
}
